/**
 * GDPR cookie consent — preference centre with Necessary / Analytics / Marketing
 * categories. Choices persist in localStorage; banner re-opens via a global
 * `window.openCookieBanner()` (used by the footer link).
 *
 * Other code may read `cookieConsent.get()` (returns the parsed prefs or null
 * if user hasn't chosen yet) before dropping non-essential cookies/scripts.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Cookie, X, Shield, BarChart3, Megaphone, Check } from "lucide-react";
import { useI18n } from "../lib/i18n";

const STORAGE_KEY = "mm_cookie_consent_v1";
const VERSION = 1;

const DEFAULT_PREFS = { necessary: true, analytics: false, marketing: false };

export const cookieConsent = {
  get() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.version !== VERSION) return null;
      return parsed.prefs;
    } catch {
      return null;
    }
  },
  set(prefs) {
    const payload = {
      version: VERSION,
      prefs: { ...DEFAULT_PREFS, ...prefs, necessary: true },
      saved_at: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: payload.prefs }));
  },
  clear() {
    localStorage.removeItem(STORAGE_KEY);
  },
};

export default function CookieConsentBanner() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const decide = () => {
      const existing = cookieConsent.get();
      if (existing) {
        setPrefs(existing);
        setOpen(false);
      } else {
        // Slight delay so the banner doesn't compete with first paint
        setTimeout(() => setOpen(true), 600);
      }
    };
    decide();
    const reopen = () => {
      const existing = cookieConsent.get();
      setPrefs(existing || DEFAULT_PREFS);
      setShowPrefs(true);
      setOpen(true);
    };
    window.openCookieBanner = reopen;
    return () => { delete window.openCookieBanner; };
  }, []);

  const acceptAll = () => {
    cookieConsent.set({ necessary: true, analytics: true, marketing: true });
    setOpen(false);
  };
  const rejectNonEssential = () => {
    cookieConsent.set({ necessary: true, analytics: false, marketing: false });
    setOpen(false);
  };
  const savePrefs = () => {
    cookieConsent.set(prefs);
    setOpen(false);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-x-0 bottom-0 z-40 p-3 md:p-5 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-3xl modal-panel pointer-events-auto p-5 md:p-6">
        {!showPrefs ? (
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="h-10 w-10 rounded-xl bg-teal/15 grid place-items-center text-teal shrink-0">
              <Cookie size={18} />
            </div>
            <div className="flex-1">
              <div className="font-serif-display text-xl">
                {t("cookies.title", "We respect your privacy.")}
              </div>
              <p className="text-sm text-muted mt-1.5">
                {t(
                  "cookies.body",
                  "We use cookies for essential site functions and, with your consent, to measure usage and personalise content. You can change preferences any time from the footer.",
                )}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={acceptAll}
                  className="btn btn-primary"
                  data-testid="cookie-accept-all"
                >
                  <Check size={14} /> {t("cookies.accept_all", "Accept all")}
                </button>
                <button
                  onClick={rejectNonEssential}
                  className="btn btn-outline"
                  data-testid="cookie-reject"
                >
                  {t("cookies.reject_non_essential", "Reject non-essential")}
                </button>
                <button
                  onClick={() => setShowPrefs(true)}
                  className="btn btn-ghost"
                  data-testid="cookie-preferences"
                >
                  {t("cookies.preferences", "Preferences")}
                </button>
              </div>
            </div>
            <button
              onClick={rejectNonEssential}
              aria-label="Close"
              className="text-muted hover:text-bone shrink-0"
              data-testid="cookie-close"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <div data-testid="cookie-prefs-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="font-serif-display text-xl">
                {t("cookies.preferences_title", "Manage cookie preferences")}
              </div>
              <button
                onClick={() => setShowPrefs(false)}
                aria-label="Back"
                className="text-muted hover:text-bone"
                data-testid="cookie-prefs-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <PrefRow
                icon={<Shield size={16} />}
                title={t("cookies.cat_necessary", "Strictly necessary")}
                desc={t(
                  "cookies.cat_necessary_desc",
                  "Required for login, language, currency and booking flows. Cannot be disabled.",
                )}
                value={true}
                disabled
                testid="cookie-toggle-necessary"
              />
              <PrefRow
                icon={<BarChart3 size={16} />}
                title={t("cookies.cat_analytics", "Analytics")}
                desc={t(
                  "cookies.cat_analytics_desc",
                  "Anonymous usage statistics so we can improve the catalog and concierge experience.",
                )}
                value={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                testid="cookie-toggle-analytics"
              />
              <PrefRow
                icon={<Megaphone size={16} />}
                title={t("cookies.cat_marketing", "Marketing")}
                desc={t(
                  "cookies.cat_marketing_desc",
                  "Personalised offers and re-engagement messages. Disabled by default.",
                )}
                value={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testid="cookie-toggle-marketing"
              />
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                onClick={rejectNonEssential}
                className="btn btn-ghost"
                data-testid="cookie-prefs-reject"
              >
                {t("cookies.reject_non_essential", "Reject non-essential")}
              </button>
              <button
                onClick={savePrefs}
                className="btn btn-primary"
                data-testid="cookie-prefs-save"
              >
                {t("cookies.save_choices", "Save my choices")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function PrefRow({ icon, title, desc, value, onChange, disabled, testid }) {
  return (
    <div className="luxe-card p-4 flex items-start gap-3" data-testid={testid}>
      <div className="h-9 w-9 rounded-lg bg-teal/10 grid place-items-center text-teal shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-bone">{title}</div>
        <p className="text-xs text-muted mt-1">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!value)}
        className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${
          value ? "bg-teal" : "bg-white/15"
        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        data-testid={`${testid}-switch`}
      >
        <span
          className={`absolute top-[2px] h-5 w-5 rounded-full bg-bone transition-transform ${
            value ? "translate-x-[22px]" : "translate-x-[2px]"
          }`}
        />
      </button>
    </div>
  );
}
